import React from 'react';
import { LOCATION_TYPES } from '../../../../constants';
import { Button } from '@orijinworks/frontend-commons';

/**
 * Returns the name of the parent location based on the given parent ID.
 *
 * @param {string} parentId - The ID of the parent location.
 * @param {Array<Object>} locations - The full list of location objects.
 * @returns {string} - The name of the parent location, or an empty string if not found.
 */
export const getParentName = (parentId, locations) => {
  if (!locations?.length) return '';
  const parent = locations.find(item => item.locationId === parentId);
  return parent?.locationName || '';
};

/**
 * Returns a comma-separated list of up to 3 child location names,
 * followed by a '[more]' indicator if there are more than 3.
 *
 * @param {string} locationId - The ID of the parent location.
 * @param {Array<Object>} locations - The full list of location objects.
 * @returns {string} - Comma-separated names of children, 'none' if no children.
 */
export const getChildren = (locationId, locations) => {
  if (!locations?.length) return 'none';
  const childLocations = locations
    .filter(item => item.parentLocationId === locationId)
    .map(item => item.locationName);

  if (!childLocations.length) return 'none';

  const display = childLocations.slice(0, 3).join(', ');
  return childLocations.length > 3 ? `${display} ... [more]` : display;
};

/**
 * Retrieves the location type based on its ID.
 *
 * @param {string} typeId - The ID of the location type.
 * @returns {string} - The location type name, or an empty string if not found.
 */
export const getLocationType = (typeId) => {
  const type = LOCATION_TYPES?.find(item => item.id === typeId);
  return type?.type || '';
};

/**
 * Formats a timestamp into a user-friendly "Last Refreshed" message.
 *
 * @param {string|Date} timestamp - The timestamp to format.
 * @returns {string} - Formatted time string (e.g., "Last Refreshed 10:30 AM").
 */
export const formatLastRefreshedTime = (timestamp) => {
  if (!timestamp) return '';
  const date = timestamp instanceof Date ? timestamp : new Date(timestamp);
  return `Last Refreshed ${date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  })}`;
};

/**
 * Formats a single location record into the structure expected by the UI grid.
 *
 * @param {Object} location - The location object to format.
 * @param {Array<Object>} allLocations - All available locations, for reference lookups.
 * @param {Function} goToLocationSettings - Callback to navigate to location settings page.
 * @returns {Object} - Formatted location data including row data and pre-defined props.
 */
export const formatLocationData = (location, allLocations, goToLocationSettings) => {
  const {
    locationId,
    locationName = '',
    locationShortCode = '',
    parentLocationId,
    locationTypeId,
    active,
    startDate,
    endDate,
  } = location;

  return {
    isSelected: false,
    rowData: {
      id: locationId,
      locationId,
      locationName,
      locationShortCode,
      parent: getParentName(parentLocationId, allLocations),
      children: getChildren(locationId, allLocations),
      locationType: getLocationType(locationTypeId),
      status: active ? 'active' : 'inactive',
      startDate,
      endDate,
      actions: () => (
        <Button
          color="primary"
          variant="contained"
          aria-label="Edit Location"
          onClick={() => goToLocationSettings(location)}
        >
          Edit
        </Button>
      ),
    },
  };
};
